import { format, useT } from "@/i18n";
import {
  EmptyState,
  FeedbackBox,
  MissingMedia,
  ReadOnlyCard,
  ScorePill,
  Section,
} from "./ReadOnlyKit";
import { SPACING, rs } from "./readOnlyStyles";
import type { ReadOnlyTemplateProps } from "./types";

const block = { display: "flex", flexDirection: "column", gap: SPACING.sm } as const;

const writing = {
  width: "100%",
  height: 140,
  borderRadius: 16,
  backgroundColor: "#FFFFFF",
  border: "1px solid #E4EAF5",
  objectFit: "contain",
} as const;

/**
 * A past handwriting play: one block per prompt word, holding what the student wrote, how it was
 * scored, and the coaching that came back. The images are the whole point of the review, so they
 * lead each block rather than sitting under the numbers.
 */
export function HandWritingReadOnly({
  snapshot,
  response,
  score,
  imageUrl,
}: ReadOnlyTemplateProps<"handWriting">) {
  const t = useT();
  if (response.attempts.length === 0) {
    return (
      <ReadOnlyCard title={snapshot.name} score={score}>
        <EmptyState message={t["readonly.noWriting"]} />
      </ReadOnlyCard>
    );
  }

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      {response.attempts.map((attempt, index) => {
        const src = imageUrl(attempt.imageId);
        return (
          <Section
            key={`${attempt.text}-${index}`}
            label={attempt.text || format(t["readonly.wordNumber"], { number: index + 1 })}
          >
            <div style={block}>
              {src ? (
                <img
                  src={src}
                  alt={format(t["readonly.writingOf"], { text: attempt.text })}
                  style={writing}
                />
              ) : (
                <MissingMedia label={t["readonly.yourWriting"]} />
              )}

              <div style={rs.answerRow}>
                <ScorePill score={attempt.score} />
              </div>

              {attempt.explanation !== "" && <FeedbackBox text={attempt.explanation} />}
            </div>
          </Section>
        );
      })}
    </ReadOnlyCard>
  );
}
